import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useSavedViews } from './saved-views.store';

/** The reader's named comparisons: save the one on screen, or open another. */
export function SavedViews({
  search,
  onOpen,
}: {
  /** The page's current query string, which is what gets saved. */
  search: string;
  onOpen: (search: string) => void;
}) {
  const { t } = useTranslation();
  const { views, full, save, remove } = useSavedViews();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Bookmark />
        {t('usage.savedViews')}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>{t('usage.savedViews')}</SheetTitle>
            <SheetDescription>{t('usage.savedViewsHelp')}</SheetDescription>
          </SheetHeader>

          <div className="space-y-5 p-4">
            <form
              className="space-y-1"
              onSubmit={(event) => {
                event.preventDefault();
                save(name, search);
                setName('');
              }}
            >
              <Label htmlFor="usage-view-name" className="text-muted-foreground text-xs">
                {t('usage.saveCurrent')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="usage-view-name"
                  value={name}
                  maxLength={60}
                  placeholder={t('usage.viewNamePlaceholder')}
                  onChange={(event) => setName(event.target.value)}
                />
                <Button type="submit" disabled={!name.trim()}>
                  {t('common.save')}
                </Button>
              </div>
              {full && (
                <p className="text-muted-foreground text-xs">
                  {t('usage.savedViewsFull')}
                </p>
              )}
            </form>

            {views.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t('usage.noSavedViews')}
              </p>
            ) : (
              <ul className="divide-y rounded-lg border">
                {views.map((view) => (
                  <li key={view.id} className="flex items-center gap-1 pr-1">
                    <button
                      type="button"
                      onClick={() => {
                        onOpen(view.search);
                        setOpen(false);
                      }}
                      className="hover:bg-accent/50 focus-visible:bg-accent/50 min-w-0 flex-1 truncate px-3 py-2 text-left text-sm font-medium outline-none"
                    >
                      {view.name}
                    </button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('usage.deleteView', { name: view.name })}
                      onClick={() => remove(view.id)}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
